/**
 * A table's Excel download, as the Console API answers it: {download: {fileName, url}, rowCount}.
 * The file is stored by the server and linked for five minutes, so the link is opened at once and
 * never kept. Used by the Transactions history, the Price history and the register extract.
 */
import { downloadLink } from './redemptionDocument.js';

/** A name the server builds: letters, digits, dot, dash and underscore, an .xlsx. Anything else is replaced. */
const SAFE_NAME = /^[A-Za-z0-9._-]{1,150}\.xlsx$/;

/**
 * What an export's answer says to do.
 *
 * @param {{ download?: { fileName?: string, url?: string }, rowCount?: number }} answer
 * @param {string} fallbackName the name to save under when the server's is not a plain one
 * @returns {{ kind: 'empty' } | { kind: 'download', url: string, fileName: string, rowCount: number | null }}
 * @throws {Error} when the answer carries no https link
 */
export function exportOutcome(answer, fallbackName) {
    const rowCount = Number.isSafeInteger(answer?.rowCount) ? answer.rowCount : null;
    if (rowCount === 0) return { kind: 'empty' };
    const url = answer?.download?.url;
    if (typeof url !== 'string' || !/^https:\/\//.test(url)) throw new Error('The export came back without its download link.');
    const named = answer.download.fileName;
    return { kind: 'download', url, fileName: SAFE_NAME.test(named ?? '') ? named : fallbackName, rowCount };
}

/**
 * Ask for an export and save it, telling the person what happened. The caller owns the button's loading state.
 *
 * @param {object} options
 * @param {() => Promise<object>} options.call the API call, e.g. () => api.register.transactionsExport(query)
 * @param {string} options.fallbackName
 * @param {{ success: Function, info: Function, error: Function }} options.message antd's
 * @param {string} options.what 'Transactions', 'Price history', 'The extract' - the failure's subject
 * @param {(url: string, fileName: string) => void} [options.save]
 * @returns {Promise<boolean>} whether a file was saved
 */
export async function downloadExport({ call, fallbackName, message, what, save = downloadLink }) {
    try {
        const outcome = exportOutcome(await call(), fallbackName);
        if (outcome.kind === 'empty') {
            message.info('No rows match these filters, so there is nothing to download.');
            return false;
        }
        save(outcome.url, outcome.fileName);
        message.success('Downloaded ' + outcome.fileName + (outcome.rowCount !== null ? ' (' + outcome.rowCount + ' rows)' : '') + '.');
        return true;
    } catch (e) {
        message.error(what + ' could not be downloaded: ' + (e?.message ?? 'try again'));
        return false;
    }
}
